import React, { useState, useEffect, useCallback } from 'react';
import { ConfirmButton } from '../components/ConfirmButton';
import { scheduleApi } from '../mocks/scheduleApi';
import {
  Plus,
  Play,
  Pause,
  Trash2,
  Edit3,
  Send,
  ChevronDown,
  ChevronUp,
  Loader2,
  Check,
  AlertCircle,
} from 'lucide-react';
import { ScheduleFormModal } from '../components/ScheduleFormModal';
import { KeywordChips } from '../components/KeywordFields';
import { EmailLog, Schedule, formatCron, formatDate } from '../types/schedule';

/** 기준 하나의 최근 성과. 로그에서 계산한다 — 집계 API 는 아직 없다. */
interface Stat {
  sent: number;      // 발송 건수(로그 줄 수)
  bids: number;      // 물어온 공고 합계
  zero: number;      // 0건으로 끝난 횟수
  failed: number;    // 실패 횟수
  lastSentAt: string | null;
}

const EMPTY_STAT: Stat = { sent: 0, bids: 0, zero: 0, failed: 0, lastSentAt: null };
const WINDOW_DAYS = 30;

function summarize(logs: EmailLog[]): Stat {
  const since = Date.now() - WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const recent = logs.filter((l) => new Date(l.sentAt).getTime() >= since);
  return {
    sent: recent.length,
    bids: recent.reduce((sum, l) => sum + l.bidCount, 0),
    zero: recent.filter((l) => l.bidCount === 0).length,
    failed: recent.filter((l) => l.status !== 'success').length,
    lastSentAt: logs[0]?.sentAt ?? null,
  };
}

export const SchedulesPage: React.FC = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Schedule | null>(null);

  const [stats, setStats] = useState<Record<number, Stat>>({});
  const [logsById, setLogsById] = useState<Record<number, EmailLog[]>>({});
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [runningId, setRunningId] = useState<number | null>(null);

  /* 기준 목록과 각 기준의 최근 로그를 함께 가져온다.
     집계 API 가 없어 FE 에서 센다 — 기준이 한 자릿수라 이걸로 충분하다.
     기준이 수십 개가 되면 BE 집계로 옮긴다. */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list: Schedule[] = await scheduleApi.list();
      setSchedules(list);

      const entries = await Promise.all(
        list.map(async (s) => {
          try {
            const data = await scheduleApi.logs(s.id);
            return [s.id, (data.items ?? []) as EmailLog[]] as const;
          } catch {
            return [s.id, [] as EmailLog[]] as const;
          }
        }),
      );
      const logMap: Record<number, EmailLog[]> = {};
      const statMap: Record<number, Stat> = {};
      for (const [id, logs] of entries) {
        logMap[id] = logs;
        statMap[id] = summarize(logs);
      }
      setLogsById(logMap);
      setStats(statMap);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* 실패를 화면에 — 전엔 catch 가 없어 켜기 · 끄기 · 지우기 실패가 보이지 않았다(#107) */
  const handleToggle = async (schedule: Schedule) => {
    try {
      await scheduleApi.update(schedule.id, { isActive: !schedule.isActive });
    } catch (err: any) {
      setError(`${schedule.isActive ? '멈추지' : '켜지'} 못했습니다 — ${err.message}`);
    }
    fetchAll();
  };

  const handleDelete = async (id: number) => {
    try {
      await scheduleApi.remove(id);
    } catch (err: any) {
      setError(`지우지 못했습니다 — ${err.message}`);
    }
    fetchAll();
  };

  const handleRunNow = async (id: number) => {
    setRunningId(id);
    try {
      await scheduleApi.run(id);
      await fetchAll();
      setNotice('지금 실행했습니다 — 결과는 이 기준의 「이력」에서 봅니다');
    } catch (err: any) {
      setError(`실행하지 못했습니다 — ${err.message}`);
    } finally {
      setRunningId(null);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (schedule: Schedule) => {
    setEditing(schedule);
    setModalOpen(true);
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">
            수집·알림
          </h1>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition flex items-center gap-1.5 flex-shrink-0"
        >
          <Plus size={16} /> 수집 기준 추가
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700 flex justify-between gap-3">
          {notice}
          <button onClick={() => setNotice('')} className="text-emerald-500 hover:text-emerald-700">×</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-16 justify-center">
          <Loader2 size={16} className="animate-spin" /> 불러오는 중...
        </div>
      ) : schedules.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 py-16 text-center">
          <p className="text-sm text-slate-500">수집 기준이 아직 없습니다.</p>
          <p className="mt-1 text-xs text-slate-400">
            공고 화면에서 조건을 잡은 뒤 그대로 수집 기준으로 저장할 수도 있습니다.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs text-slate-400">
                <th className="text-left font-medium px-4 py-2.5 w-8"></th>
                <th className="text-left font-medium px-2 py-2.5">이름</th>
                <th className="text-left font-medium px-2 py-2.5">주기</th>
                <th className="text-left font-medium px-2 py-2.5">최근 발송</th>
                <th className="text-right font-medium px-2 py-2.5">발송</th>
                <th className="text-right font-medium px-2 py-2.5">공고 합계</th>
                <th className="text-right font-medium px-2 py-2.5">평균</th>
                <th className="text-right font-medium px-2 py-2.5">0건</th>
                <th className="text-right font-medium px-2 py-2.5">실패</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((s) => {
                const stat = stats[s.id] ?? EMPTY_STAT;
                const avg = stat.sent > 0 ? (stat.bids / stat.sent).toFixed(1) : '—';
                const open = expandedId === s.id;
                const logs = logsById[s.id] ?? [];
                return (
                  <React.Fragment key={s.id}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50 align-middle">
                      <td className="px-4 py-2.5">
                        <button
                          onClick={() => setExpandedId(open ? null : s.id)}
                          className="text-slate-400 hover:text-slate-600"
                          title="발송 이력"
                        >
                          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </td>
                      <td className="px-2 py-2.5">
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                              s.isActive ? 'bg-emerald-500' : 'bg-slate-300'
                            }`}
                            title={s.isActive ? '동작 중' : '멈춤'}
                          />
                          <span className="font-medium text-slate-900">{s.name}</span>
                        </div>
                        {(s.keywords || s.excludeKeywords || s.institution) && (
                          <div className="mt-1 ml-3.5 flex max-w-md flex-wrap items-center gap-1 text-xs text-slate-400">
                            <KeywordChips value={{ keyword: s.keywords || '', exclude: s.excludeKeywords || '' }} />
                            {s.institution && <span>{s.institution}</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2.5 text-slate-500 whitespace-nowrap">
                        {formatCron(s.cronExpression)}
                      </td>
                      <td className="px-2 py-2.5 text-slate-500 whitespace-nowrap">
                        {formatDate(stat.lastSentAt ?? s.lastRunAt)}
                      </td>
                      <td className="px-2 py-2.5 text-right text-slate-700">{stat.sent || '—'}</td>
                      <td className="px-2 py-2.5 text-right text-slate-700">{stat.bids || '—'}</td>
                      <td className="px-2 py-2.5 text-right font-medium text-slate-900">{avg}</td>
                      <td className="px-2 py-2.5 text-right">
                        <span className={stat.zero > 0 ? 'text-amber-600' : 'text-slate-400'}>
                          {stat.zero || '—'}
                        </span>
                      </td>
                      <td className="px-2 py-2.5 text-right">
                        <span className={stat.failed > 0 ? 'text-red-600' : 'text-slate-400'}>
                          {stat.failed || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          {/* 실제 메일이 나갈 수 있다 — 한 번 더 누르게 한다(#108) */}
                          <ConfirmButton
                            onConfirm={() => handleRunNow(s.id)}
                            disabled={runningId === s.id}
                            title="지금 실행"
                            ask="한 번 더 — 지금 조회 · 발송"
                            armedClassName="rounded-lg px-2 py-1 text-xs font-medium bg-indigo-600 text-white whitespace-nowrap"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40"
                          >
                            {runningId === s.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Send size={14} />
                            )}
                          </ConfirmButton>
                          <button
                            onClick={() => handleToggle(s)}
                            title={s.isActive ? '멈추기' : '켜기'}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                          >
                            {s.isActive ? <Pause size={14} /> : <Play size={14} />}
                          </button>
                          <button
                            onClick={() => openEdit(s)}
                            title="고치기"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                          >
                            <Edit3 size={14} />
                          </button>
                          <ConfirmButton
                            onConfirm={() => handleDelete(s.id)}
                            title="지우기"
                            ask="한 번 더 — 지웁니다"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                          </ConfirmButton>
                        </div>
                      </td>
                    </tr>

                    {open && (
                      <tr className="bg-slate-50 border-b border-slate-100">
                        <td colSpan={10} className="px-4 py-3">
                          {logs.length === 0 ? (
                            <p className="text-xs text-slate-400">발송 이력이 없습니다.</p>
                          ) : (
                            <ul className="space-y-1">
                              {logs.slice(0, 10).map((log) => (
                                <li key={log.id} className="flex items-center gap-3 text-xs">
                                  {log.status === 'success' ? (
                                    <Check size={12} className="text-emerald-500 flex-shrink-0" />
                                  ) : (
                                    <AlertCircle size={12} className="text-red-500 flex-shrink-0" />
                                  )}
                                  <span className="text-slate-500 w-40">{formatDate(log.sentAt)}</span>
                                  <span className="text-slate-700 w-16">{log.bidCount}건</span>
                                  <span className="text-slate-400 truncate">
                                    {log.errorMessage || log.emailTo}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 0건이 기록되지 않는다는 것을 화면에도 적어 둔다 —
          평균이 실제보다 높게 보이기 때문이다 (schedule.service.ts executeSchedule) */}
      {schedules.length > 0 && (
        <p className="mt-3 text-xs text-slate-400">
          공고가 0건이면 메일을 보내지 않고 이력도 남기지 않습니다 — 「0건」 열은 발송된 것 중
          0건이었던 횟수이며, 평균은 실제보다 높게 나옵니다.
        </p>
      )}

      <ScheduleFormModal
        open={modalOpen}
        editing={editing}
        onClose={() => setModalOpen(false)}
        onSaved={fetchAll}
      />

    </div>
  );
};
